
/** A single seat row after normalization, regardless of which source format it came from. */
interface ISeatRow {
    Section: string;
    Row: string;
    SeatName: string;
    Available: string;
    Price: number | null;
}

/** One seat entry as stored under `inBOnly[section][row]` in the "modified" JSON format. */
interface IModifiedSeat {
    name: string;
    price: number | null;
}

/** "Modified" JSON shape: seats grouped by section, then by row. */
interface IModifiedSeatData {
    inBOnly?: Record<string, Record<string, IModifiedSeat[]>>;
}

/** One seat entry in the "original" JSON format's flat `Seats` array. */
interface IOriginalSeat {
    Sec: string;
    Row: string;
    SeatName: string;
    Available: string;
    Price: number | null;
}

/** "Original" JSON shape: a flat array of seats. */
interface IOriginalSeatData {
    Seats?: IOriginalSeat[];
}

/** One consolidated row: available seat count grouped by Section, Row, and Price. */
interface IConsolidatedRow {
    Section: string;
    Row: string;
    Price: number | null;
    'Number of Seats': number;
}

/**
 * Parses a modified JSON file with an "inBOnly" key.
 * @param data - The modified seat data object.
 * @returns Array of seat objects.
 */
function parseModifiedSeatData (data: IModifiedSeatData): ISeatRow[] {
    const modifiedData = data.inBOnly || {};
    const result: ISeatRow[] = [];

    for (const section in modifiedData) {
        const rowsData = modifiedData[section];
        for (const row in rowsData) {
            const seats = rowsData[row];
            seats.forEach(seat => {
                result.push({
                    Section: section,
                    Row: row,
                    SeatName: seat.name,
                    // Assume available if not provided.
                    Available: 'Y',
                    Price: seat.price
                });
            });
        }
    }
    return result;
}

/**
 * Parses an original JSON file with a "Seats" key.
 * @param data - The original seat data object.
 * @returns Array of seat objects.
 */
function parseOriginalSeatData (data: IOriginalSeatData): ISeatRow[] {
    const seatData = data.Seats || [];
    return seatData.map(seat => ({
        Section: seat.Sec,
        Row: seat.Row,
        SeatName: seat.SeatName,
        Available: seat.Available,
        Price: seat.Price
    }));
}
/**
 * Processes an array of seat objects.
 * Filters for available seats, groups by Section, Row, and Price,
 * and sorts the output accordingly.
 * @param data - Array of seat objects.
 * @returns Consolidated and sorted data.
 */
function processData (data: ISeatRow[]): IConsolidatedRow[] {
    // Filter for available seats only
    const availableData = data.filter(item => item.Available === 'Y');

    // Group by Section, Row, and Price, counting the seats in each group.
    const grouped: Record<string, IConsolidatedRow> = {};
    availableData.forEach(item => {
        const key = `${item.Section}_${item.Row}_${item.Price}`;
        if (!grouped[key]) {
            grouped[key] = { Section: item.Section, Row: item.Row, Price: item.Price, 'Number of Seats': 0 };
        }
        grouped[key]['Number of Seats'] += 1;
    });

    // Convert the grouped data into an array.
    const consolidatedData = Object.values(grouped);

    // Sort by Section, then Row, then Price
    consolidatedData.sort((a, b) => {
        if (a.Section < b.Section) return -1;
        if (a.Section > b.Section) return 1;
        if (a.Row < b.Row) return -1;
        if (a.Row > b.Row) return 1;
        return (a.Price ?? 0) - (b.Price ?? 0);
    });

    return consolidatedData;
}

/**
 * Detects the seat-data source format and returns the consolidated, sorted result.
 * @param data - Either the "original" (`Seats`) or "modified" (`inBOnly`) JSON shape.
 * @returns Consolidated and sorted data, or an empty array if the shape is unrecognized.
 */
export function getConsolidatedData (data: IOriginalSeatData & IModifiedSeatData): IConsolidatedRow[] {
    let parsedData: ISeatRow[] | null = null;
    if (data.Seats) {
        console.log('Identified as original data');
        parsedData = parseOriginalSeatData(data);
    } else if (data.inBOnly) {
        console.log('Identified as modified data');
        parsedData = parseModifiedSeatData(data);
    } else {
        console.log('Unknown data type. Skipping.');
        return [];
    }
    return processData(parsedData);
}
